import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../../lib/utils";
import { calculatePosition } from "./positioningUtils";

interface WebViewTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  className?: string;
  showOnlyWhenTruncated?: boolean;
}

export const WebViewTooltip: React.FC<WebViewTooltipProps> = ({
  children,
  content,
  side = "bottom",
  sideOffset = 8,
  className,
  showOnlyWhenTruncated = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isTruncated, setIsTruncated] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tooltipId = useRef(
    `tooltip-${Math.random().toString(36).substring(2, 11)}`
  ).current;

  const updatePosition = () => {
    if (!triggerRef.current || !contentRef.current) return;

    if (
      !document.contains(triggerRef.current) ||
      !document.contains(contentRef.current)
    ) {
      return;
    }

    const triggerElement = triggerRef.current.firstElementChild as HTMLElement;
    if (!triggerElement) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    setPosition(
      calculatePosition({
        side,
        sideOffset,
        triggerRect,
        contentWidth: contentRect.width,
        contentHeight: contentRect.height,
        padding: 12,
        narrowBreakpoint: 480
      })
    );
  };

  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure DOM is ready for measurement
      requestAnimationFrame(() => {
        updatePosition();
      });
      const handleResize = () => updatePosition();
      window.addEventListener("resize", handleResize);
      return () => {
        try {
          window.removeEventListener("resize", handleResize);
        } catch {
          // Ignore cleanup errors
        }
      };
    }
  }, [isOpen, side, sideOffset, content]);

  // Truncation detection for conditional tooltip display
  useEffect(() => {
    if (!showOnlyWhenTruncated || !triggerRef.current) return;

    const checkTruncation = () => {
      // Find the span element that contains the text (not the icons)
      // For badges, look for the inner span with text content
      // For customer names, look for the span with text content (may be nested in div with role="presentation")
      const element =
        (triggerRef.current?.querySelector("span span") as HTMLElement) ||
        (triggerRef.current?.querySelector(
          "div[role='presentation'] span"
        ) as HTMLElement) ||
        (triggerRef.current?.querySelector("span") as HTMLElement);
      if (element) {
        // Check if the element has text content and is actually truncated
        const hasText = Boolean(
          element.textContent && element.textContent.trim().length > 0
        );
        const truncated = hasText && element.scrollWidth > element.clientWidth;
        setIsTruncated(truncated);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(checkTruncation);
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [showOnlyWhenTruncated]);

  return (
    <>
      <div
        ref={triggerRef}
        style={{ display: "contents" }}
        aria-describedby={isOpen ? tooltipId : undefined}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        {children}
      </div>

      {isOpen &&
        (!showOnlyWhenTruncated || isTruncated) &&
        createPortal(
          <div
            id={tooltipId}
            ref={contentRef}
            role="tooltip"
            className={cn(
              "webview-tooltip",
              "fixed z-[99999] overflow-hidden rounded-md px-3 py-1.5 text-xs shadow-md",
              "animate-in fade-in-0 zoom-in-95",
              className
            )}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};
